import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import AddIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined"
import EditIcon from "@mui/icons-material/EditOutlined"
import LockIcon from "@mui/icons-material/LockOutlined"
import VerifiedIcon from "@mui/icons-material/VerifiedOutlined"
import TimeIcon from "@mui/icons-material/ScheduleOutlined"

import { taskApi, timeApi } from "../api"
import { useAuth } from "../context/AuthContext"
import { usePermissions } from "../permissions"
import { useTimer } from "../context/TimerContext"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows } from "../components/LoadingState"
import { humanDuration, decimalHours } from "../time/duration"
import { MOTION, SUCCESS_FILL, transition } from "../theme"
import { filledChipSx } from "../theme/contrast"
import { formatDateTime } from "../utils/format"
import { rise } from "../motion"
import { addDays, groupByDay, isToday, isoDay, weekLabel, weekRange } from "./time/weeks"
import LogTimeDialog from "./time/LogTimeDialog"
import TaskDetailDrawer from "./tasks/TaskDetailDrawer"
import ProjectDetailDialog from "./projects/ProjectDetailDialog"

/** The sign-off green. Not the brand green: this is a state, and the brand is
 *  everywhere — a status people scan for has to be the odd one out. The fill
 *  step rather than the bright teal, because the chip has a word on it. */
const SIGNED_COLOR = SUCCESS_FILL

/** A working week, for the bar behind each day. Display only. */
const TARGET_WEEK_SECONDS = 40 * 3600

/**
 * Your own week.
 *
 * ── Why a week and not a list ───────────────────────────────────────────────
 *
 * The question somebody actually has is "what did I do on Tuesday, and have I
 * missed anything". A reverse-chronological list answers neither: the gaps are
 * invisible, which is exactly where the unlogged hours are.
 */
export default function TimesheetPage() {
  const { can } = usePermissions()
  const { running, elapsed } = useTimer()
  const { user } = useAuth()

  const [anchor, setAnchor] = useState(() => new Date())
  const [entries, setEntries] = useState([])
  const [totals, setTotals] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [dialog, setDialog] = useState(null)
  /* One piece of state each, holding an id. A boolean beside an id can
     disagree with it, and the disagreement always shows as an empty panel. */
  const [openTaskId, setOpenTaskId] = useState(null)
  const [openProjectId, setOpenProjectId] = useState(null)
  const [statuses, setStatuses] = useState([])

  const range = useMemo(() => weekRange(anchor), [anchor])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await timeApi.entries({ from: isoDay(range.from), to: isoDay(range.to), limit: 500 })
      setEntries(data.entries || [])
      setTotals(data.totals || null)
      setError(null)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [range.from, range.to])

  useEffect(() => {
    load()
  }, [load])

  /* The board columns, for the task panel. Fetched once here rather than by the
     panel itself, so opening a task from a row is not a second round trip
     before anything appears. */
  useEffect(() => {
    if (!can("tasks", "view")) return
    taskApi
      .statuses()
      .then((data) => setStatuses(data.statuses || []))
      .catch(() => setStatuses([]))
  }, [can])

  // A timer stopping adds a row to today. Reloading on that transition keeps the
  // week honest without polling it.
  useEffect(() => {
    if (!running) load()
  }, [running, load])

  const days = useMemo(() => groupByDay(entries, anchor), [entries, anchor])
  const weekSeconds = days.reduce((sum, d) => sum + d.seconds, 0)

  /*
   * ── Whose week is on the screen ───────────────────────────────────────────
   *
   * A manager has `view_all`, and this page asks for the week without naming a
   * person — so what comes back is the team's, not just theirs. That is useful,
   * and it was also unsigned: other people's rows carried a name and the
   * viewer's carried nothing, so a blank meant "yours" only if you already knew
   * the rule. Now every row is attributed when there is more than one person in
   * the week, and the header separates the total from the viewer's own share.
   */
  const mine = useMemo(() => {
    if (!user?.id) return { seconds: 0, shared: false }
    let seconds = 0
    let shared = false
    for (const entry of entries) {
      if (Number(entry.user_id) === Number(user.id)) seconds += Number(entry.duration_seconds || 0)
      else if (entry.user_id != null) shared = true
    }
    return { seconds, shared }
  }, [entries, user?.id])

  const remove = async (entry) => {
    if (!window.confirm(`Delete "${entry.description || "this entry"}"? ${humanDuration(entry.duration_seconds)} will be removed from your timesheet.`)) return
    try {
      await timeApi.deleteEntry(entry.id)
      setNotice("Entry deleted.")
      load()
    } catch (err) {
      setError(err.userMessage)
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="My timesheet"
        subtitle={
          mine.shared
            ? "Your week, alongside the hours your team logged in it."
            : "Everything you have logged, week by week."
        }
        actions={
          can("time_tracking", "log") ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ mode: "create" })}>
              Log time
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      ) : null}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <IconButton size="small" onClick={() => setAnchor(addDays(anchor, -7))} aria-label="Previous week">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h3" component="p" sx={{ minWidth: 150 }}>
              {weekLabel(anchor)}
            </Typography>
            <IconButton size="small" onClick={() => setAnchor(addDays(anchor, 7))} aria-label="Next week">
              <ChevronRightIcon />
            </IconButton>
            <Button size="small" onClick={() => setAnchor(new Date())}>
              This week
            </Button>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Tracked
                </Typography>
                <Typography variant="h3" component="p" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {decimalHours(weekSeconds)} h
                </Typography>
              </Box>
              {/* Only once the week holds somebody else's hours: on your own
                  week "Tracked" and "Yours" are the same number twice. */}
              {mine.shared ? (
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Yours
                  </Typography>
                  <Typography variant="h3" component="p" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {decimalHours(mine.seconds)} h
                  </Typography>
                </Box>
              ) : null}
              {totals ? (
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Billable
                  </Typography>
                  <Typography variant="h3" component="p" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {decimalHours(totals.billable_seconds)} h
                  </Typography>
                </Box>
              ) : null}
              {/* Only when there is something waiting. A column reading 0.00 h
                  every week trains people to stop seeing it, and then it is
                  still 0.00 h in their head on the week it is not. */}
              {totals && Number(totals.unsigned_seconds) > 0 ? (
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Awaiting sign-off
                  </Typography>
                  <Typography
                    variant="h3"
                    component="p"
                    sx={{ fontVariantNumeric: "tabular-nums", color: "warning.dark" }}
                  >
                    {decimalHours(totals.unsigned_seconds)} h
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={Math.min(100, (weekSeconds / TARGET_WEEK_SECONDS) * 100)}
            sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
          />
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent>
            <LoadingRows rows={7} />
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {days.map((day, index) => (
            <Card
              key={day.key}
              sx={{
                // Today is the row somebody is adding to, so it is the one that
                // should be findable without reading the dates.
                borderColor: isToday(day.date) ? "primary.main" : undefined,
                borderWidth: isToday(day.date) ? 2 : 1,
                borderStyle: "solid",
                transition: transition("border-color", MOTION.base),
                ...rise(index),
              }}
            >
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 118 }}>
                    {day.date.toLocaleDateString(undefined, { weekday: "long" })}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                      {day.date.getDate()}
                    </Typography>
                  </Typography>
                  {isToday(day.date) ? <Chip size="small" label="Today" color="primary" sx={{ height: 20, fontSize: 11 }} /> : null}
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="body2" color={day.seconds > 0 ? "text.primary" : "text.disabled"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {day.seconds > 0 ? humanDuration(day.seconds) : "—"}
                  </Typography>
                  {can("time_tracking", "log") ? (
                    <Tooltip title="Log time on this day">
                      <IconButton
                        size="small"
                        onClick={() => setDialog({ mode: "create", date: day.date })}
                        aria-label={`Log time on ${day.date.toDateString()}`}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>

                {day.entries.length > 0 ? (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={0.5}>
                      {day.entries.map((entry) => (
                        <EntryRow
                          key={entry.id}
                          entry={entry}
                          isRunning={running?.id === entry.id}
                          elapsed={elapsed}
                          canEdit={can("time_tracking", "edit_own")}
                          canDelete={can("time_tracking", "delete_own")}
                          onEdit={() => setDialog({ mode: "edit", entry })}
                          onDelete={() => remove(entry)}
                          viewerId={user?.id}
                          attributed={mine.shared}
                          onOpenTask={setOpenTaskId}
                          onOpenProject={setOpenProjectId}
                        />
                      ))}
                    </Stack>
                  </>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {!loading && entries.length === 0 ? (
        <Box sx={{ mt: 2 }}>
          <EmptyState
            icon={TimeIcon}
            title="Nothing logged this week"
            description="Start a timer from a task, or log time by hand with the button above."
          />
        </Box>
      ) : null}

      {/* The same panel Tasks uses. A task opened from a timesheet row is the
          same task, and a second, thinner version of it would drift. */}
      <TaskDetailDrawer taskId={openTaskId} statuses={statuses} onClose={() => setOpenTaskId(null)} onChanged={load} />
      <ProjectDetailDialog projectId={openProjectId} onClose={() => setOpenProjectId(null)} />

      <LogTimeDialog
        open={Boolean(dialog)}
        mode={dialog?.mode}
        entry={dialog?.entry}
        defaultDate={dialog?.date}
        onClose={() => setDialog(null)}
        onSaved={(message) => {
          setDialog(null)
          setNotice(message)
          load()
        }}
      />
    </Box>
  )
}

/**
 * One logged stint.
 *
 * ── Why the state is a word and not an icon ─────────────────────────────────
 *
 * It was a tick and a padlock, each with a tooltip. Somebody looking at their
 * week could not tell what had been signed off and what had not without hovering
 * every row one at a time — and a tooltip is not available at all on a tablet.
 * Sign-off is the difference between hours that can reach an invoice and hours
 * that cannot, so it is the one thing on the row that has to be readable at a
 * glance.
 *
 * ── Why every row is signed, or none of them are ────────────────────────────
 *
 * On a week that is only yours, a name on every row is forty repetitions of
 * something you already know, so there is none. The moment somebody else's
 * hours are in the same list the rule flips and all of them are attributed —
 * including yours, as "You". Naming only the others leaves your own rows blank,
 * and a blank is not an answer to "whose is this": it is indistinguishable from
 * a row whose owner failed to load.
 */
function EntryRow({ entry, isRunning, elapsed, canEdit, canDelete, onEdit, onDelete, viewerId, attributed, onOpenTask, onOpenProject }) {
  const locked = entry.invoice_line_id != null
  const signed = entry.signed_off_at != null
  const someoneElse = viewerId != null && entry.user_id != null && Number(entry.user_id) !== Number(viewerId)
  /* "You" rather than your own name, and only where names are being shown at
     all. Printing the name would be correct and read as somebody else's row —
     the word that answers "is this mine" is the first person. */
  const whose = attributed ? (someoneElse ? entry.user_name : "You") : null

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1.5,
        transition: transition("background-color", MOTION.fast),
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap>
          {entry.task_code ? (
            <Box
              component="span"
              role="button"
              tabIndex={0}
              aria-label={`Open ${entry.task_code}`}
              onClick={() => onOpenTask?.(entry.task_id)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return
                event.preventDefault()
                onOpenTask?.(entry.task_id)
              }}
              sx={{
                mr: 0.75,
                fontWeight: 600,
                cursor: "pointer",
                color: "primary.dark",
                borderRadius: 0.5,
                "&:hover": { textDecoration: "underline" },
                "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.dark}`, outlineOffset: 2 },
              }}
            >
              {entry.task_code}
            </Box>
          ) : null}
          {entry.description || entry.task_title || "Untitled"}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {entry.client_label || null}
          {entry.client_label && entry.project_name ? " · " : null}
          {/* The project is the other thing a row raises a question about, and
              the answer is two clicks and a lost week away otherwise. */}
          {entry.project_name ? (
            <Box
              component="span"
              role="button"
              tabIndex={0}
              aria-label={`Open ${entry.project_name}`}
              onClick={() => onOpenProject?.(entry.project_id)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return
                event.preventDefault()
                onOpenProject?.(entry.project_id)
              }}
              sx={{
                cursor: "pointer",
                color: "primary.dark",
                borderRadius: 0.5,
                "&:hover": { textDecoration: "underline" },
                "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.dark}`, outlineOffset: 2 },
              }}
            >
              {entry.project_name}
            </Box>
          ) : null}
          {!entry.client_label && !entry.project_name ? "No project" : null}
          {whose ? (
            <Box component="span" sx={{ fontWeight: someoneElse ? 500 : 700 }}>
              {` · ${whose}`}
            </Box>
          ) : null}
        </Typography>
      </Box>

      {!entry.is_billable ? (
        <Chip size="small" label="Not billable" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
      ) : null}

      {/* Three states, in the order they happen: logged, signed off, invoiced.
          Only the last two get a chip — "logged" is what every row on a
          timesheet already is, and labelling it would be forty chips saying the
          name of the screen. */}
      {signed && !locked ? (
        <Tooltip title={`Signed off by ${entry.signed_off_by_name || "a manager"} on ${formatDateTime(entry.signed_off_at)}`}>
          <Chip
            size="small"
            icon={<VerifiedIcon sx={{ fontSize: 13 }} />}
            label="Signed off"
            sx={{ height: 20, fontSize: 10, ...filledChipSx(SIGNED_COLOR) }}
          />
        </Tooltip>
      ) : null}
      {!signed && !locked ? (
        <Tooltip title="Still yours to change. A manager has not signed this off yet, so it cannot reach an invoice.">
          <Chip size="small" label="Awaiting sign-off" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
        </Tooltip>
      ) : null}
      {locked ? (
        <Tooltip title={`On an invoice — this entry can no longer be changed${entry.signed_off_by_name ? `. Signed off by ${entry.signed_off_by_name}.` : ""}`}>
          <Chip
            size="small"
            icon={<LockIcon sx={{ fontSize: 13 }} />}
            label="Invoiced"
            sx={{ height: 20, fontSize: 10 }}
          />
        </Tooltip>
      ) : null}

      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", minWidth: 62, textAlign: "right" }}>
        {isRunning ? humanDuration(elapsed) : humanDuration(entry.duration_seconds)}
      </Typography>

      {/* Hidden rather than disabled on a locked entry: a greyed button people
          keep pressing is worse than no button, and the padlock already explains. */}
      {canEdit && !locked && !isRunning ? (
        <IconButton size="small" onClick={onEdit} aria-label="Edit this entry">
          <EditIcon fontSize="small" />
        </IconButton>
      ) : null}
      {canDelete && !locked && !isRunning ? (
        <IconButton size="small" onClick={onDelete} aria-label="Delete this entry">
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Stack>
  )
}
